// frontend/src/features/livestock/lineUpReference.ts
// The reference photo every photo of a subject is lined up against: for a tank,
// its chosen cover (docs/plans/2026-10-29-tank-shot-framing-design.md, 3; an
// automatic cover, the latest shot, is not a reference, since it changes with
// every upload); for an organism, the reference photo chosen for it
// (docs/plans/2026-10-29-livestock-compare-design.md, 5).

import type { Livestock } from '@/types/livestock';
import type { PhotoFraming } from '@/types/photos';
import type { Tank } from '@/types/tank';

export interface LineUpReference {
  id: number;
  /** Signed URL of the reference's display variant (800 px): the ghost. */
  displayUrl: string | null;
  /** The reference's own framing: the ghost is drawn as it was lined up. */
  framing: PhotoFraming | null;
}

export function referenceOf(tank: Tank | null | undefined): LineUpReference | null {
  if (!tank?.cover_photo_id) return null;
  return {
    id: tank.cover_photo_id,
    displayUrl: tank.cover_display_url ?? null,
    framing: tank.cover_framing ?? null,
  };
}

/** The organism's reference photo; its URL and framing come with the detail read only. */
export function organismReferenceOf(
  livestock: Livestock | null | undefined
): LineUpReference | null {
  if (!livestock?.reference_entry_id) return null;
  return {
    id: livestock.reference_entry_id,
    displayUrl: livestock.reference_display_url ?? null,
    framing: livestock.reference_framing ?? null,
  };
}

/**
 * What Line up does for a shot: 'reference' (it is the reference: nothing to
 * line up), 'choose' (no reference yet: offer to make this shot it), 'edit'.
 */
export function lineUpMode(
  shotId: number,
  reference: LineUpReference | null
): 'reference' | 'choose' | 'edit' {
  if (!reference) return 'choose';
  return reference.id === shotId ? 'reference' : 'edit';
}

/** The shot was lined up against a shot that is no longer the reference. */
export function linedUpToEarlierReference(
  framing: PhotoFraming | null | undefined,
  reference: LineUpReference | null
): boolean {
  return !!framing && !!reference && framing.ref_photo_id !== reference.id;
}
